<template>
    <div class="conformity well well-no-shadow fs-3">
        <h2 class="mt-0 mb-1">
            {{ $l10n.__('Attestation de conformité') }}
        </h2>
        <p class="mt-0">
            {{
                $l10n.__(
                    'Retrouvez dans cette page les informations concernant la conformité de votre compte SELLSY.',
                )
            }}
            <CompassLink
                rel="noopener noreferrer"
                :label="$t.__('common.labels.learn_more')"
                link="https://help.sellsy.com/hc/"
                target="_blank"
            />
        </p>

        <div v-loading="isLoading" class="b-1 flex-row py-4 px-3 ai-center">
            <div>
                <p class="m-0 semibold">{{ mainLabel }}</p>
                <template v-if="isConform">
                    <p class="m-0 gray-60">
                        <span v-if="!isToday && !isYesterday">{{ dateLabel }}</span>
                        {{ $filters.dateFormatter(activatedAt, 'NORMAL_DATE_TIME') }}
                    </p>
                    <p class="m-0 gray-60">{{ userLabel }}</p>
                </template>
                <p v-else class="m-0 gray-60">{{ secondaryLabel }}</p>
                <p v-if="isConform && isTrial" class="mb-0 mt-2 red semibold">{{ trialLabel }}</p>
            </div>
            <div class="flex-item-right">
                <el-link
                    v-if="isConform"
                    type="primary"
                    :disabled="!pdf || isTrial"
                    class="btn btn-primary"
                    :class="{ 'btn-disabled': !pdf || isTrial }"
                    :href="gatePdf"
                    target="_blank"
                    :download="
                        $t.__('settings.sellsymodule.invoicing.Conformity.conformity_certificate')
                    "
                >
                    {{
                        pdf
                            ? $l10n.__('Télécharger mon attestation')
                            : $l10n.__('En cours de génération')
                    }}
                </el-link>
                <CompassButton
                    v-else
                    :label="
                        $t.__('settings.sellsymodule.invoicing.Conformity.enable_conformity_mode')
                    "
                    :disabled="!iscreatable"
                    @click="displayForm = true"
                />
            </div>
        </div>
        <!-- POPUP -->
        <CompassModal
            v-model:visible="displayForm"
            :title="
                $t.__(
                    'settings.sellsymodule.invoicing.Conformity.enable_conformity_mode_on_account',
                )
            "
            size="large"
            class="conformity"
            @closed="closeModal"
        >
            <template #default>
                <div class="dialog__default">
                    <el-form v-if="!isConfirm" v-loading="loading">
                        <h3 class="uppercase typographies-heading-h3">
                            {{ $l10n.__('Important') }}
                        </h3>
                        <p class="typographies-paragraph-medium-semibold mb-4">
                            {{ $l10n.__('Conformité de votre compte') }}
                        </p>

                        <p class="mb-3 typographies-paragraph-medium-regular">
                            {{
                                $l10n.__(
                                    'Nous souhaitons vous proposer de rendre votre compte conforme à la législation française en vigueur. Pour cela, plusieurs paramètres seront modifiés pouvant impacter votre utilisation de SELLSY. ',
                                )
                            }}
                        </p>
                        <p class="typographies-paragraph-medium-semibold">
                            {{
                                $l10n.__(
                                    'Les modifications de paramètres nécessaires à cette mise en conformité sont les suivants:',
                                )
                            }}
                        </p>

                        <el-form-item
                            v-for="(condition, index) in conditions"
                            :key="index"
                            class="b-1 p-4 mt-3"
                        >
                            <p class="typographies-paragraph-medium-semibold fix-margin-p">
                                {{ condition.title }}
                            </p>
                            <p class="my-3 typographies-paragraph-medium-regular">
                                {{ condition.content }}
                            </p>
                            <el-checkbox
                                v-model="condition.accept"
                                class="typographies-paragraph-medium-semibold"
                            >
                                {{ condition.label }}
                            </el-checkbox>
                        </el-form-item>
                        <p
                            class="typographies-paragraph-medium-semibold bt-4-red bb-4-red py-3 mt-6 px-4"
                            v-html="disclaimer"
                        ></p>

                        <el-form-item class="mt-6 px-4">
                            <el-checkbox
                                v-model="acceptGlobal"
                                class="typographies-paragraph-medium-semibold agree-checkbox"
                            >
                                {{
                                    $l10n.__(
                                        'J’ai pris connaissance des futures modifications apportées à mon compte SELLSY et du choix définitif de leur application.',
                                    )
                                }}
                            </el-checkbox>
                        </el-form-item>
                    </el-form>
                    <div v-else class="confirm-section flex-column ai-center">
                        <i class="icon-email-statut-valid success"></i>
                        <p class="typographies-paragraph-medium-semibold fs-5 mt-4">
                            {{ $l10n.__('Votre compte est désormais conforme') }}
                        </p>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="dialog__footer">
                    <div v-if="!isConfirm">
                        <CompassButton
                            :label="$t.__('common.actions.cancel')"
                            type="secondary"
                            @click="displayForm = false"
                        />
                        <CompassButton
                            :label="
                                $t.__(
                                    'settings.sellsymodule.invoicing.Conformity.make_account_compliant',
                                )
                            "
                            :disabled="!allAccept"
                            data-bot="compliance_enable-compliant-mode--bring-my-account-into-compliance"
                            class="ml-3"
                            @click="confirm"
                        />
                    </div>
                    <div v-else>
                        <CompassButton
                            :label="$t.__('common.actions.back_settings')"
                            type="primary"
                            class="mt-5"
                            @click="displayForm = false"
                        />
                    </div>
                </div>
            </template>
        </CompassModal>
    </div>
</template>
<script>
import { createNamespacedHelpers } from 'vuex';

const corpMapState = createNamespacedHelpers('corp').mapState;

export default {
    props: {
        iscreatable: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            isLoading: false,
            displayForm: false,
            isConform: false,
            activatedAt: null,
            activatedBy: null,
            pdf: null,

            loading: false,
            acceptGlobal: false,
            isConfirm: false,
            conformityResponse: null,
            disclaimer: this.$l10n.__(
                'Nous attirons votre attention sur le fait que %(redStart)s ce choix de mise en conformité est définitif. %(redEnd)s Il sera impossible de revenir en arrière, que ce soit par une action de votre part ou de la part de SELLSY.',
                { redStart: '<span class="red">', redEnd: '</span>' },
            ),
            conditions: [
                {
                    title: this.$l10n.__(
                        'Création obligatoire d’un avoir pour la modification ou l’annulation d’une facture',
                    ),
                    content: this.$l10n.__(
                        'Toutes modifications d’une facture ou annulation de celle-ci entraînera automatiquement la création d’un avoir.',
                    ),
                    label: this.$l10n.__(
                        'Je confirme avoir pris connaissance de cette modification',
                    ),
                    accept: false,
                },
                {
                    title: this.$l10n.__('Une numérotation continue et chronologique'),
                    content: this.$l10n.__(
                        'La numérotation de vos factures et avoirs doit être continue (sans trou de numérotation) et chronologique (la numéro 11 doit être datée avant la numéro 12 par exemple). Pour cela, la numérotation ne pourra pas être modifiée et sera définie par votre compte SELLSY. Il sera impossible de dater une facture dans le futur et avant la dernière facture émise.',
                    ),
                    label: this.$l10n.__(
                        'Je confirme avoir pris connaissance de cette modification',
                    ),
                    accept: false,
                },
            ],
        };
    },
    computed: {
        ...corpMapState(['accountLevel']),
        isTrial() {
            return this.accountLevel === 'trial';
        },
        mainLabel() {
            return this.isConform
                ? this.$l10n.__('Votre compte SELLSY est conforme.')
                : this.$l10n.__('Votre compte SELLSY n’est pas conforme à la loi anti-fraude.');
        },
        secondaryLabel() {
            return this.$l10n.__(
                'Le mode conforme doit être activé pour obtenir une attestation de conformité.',
            );
        },
        trialLabel() {
            return this.$l10n.__(
                'Le téléchargement de l’attestation est indisponible sur la version d’essai.',
            );
        },
        dateLabel() {
            return this.$l10n.__('Depuis le');
        },
        userLabel() {
            return this.$l10n.__(
                `Par : ${this.activatedBy?.firstname} ${this.activatedBy?.lastname} - ${this.activatedBy?.email}`,
            );
        },
        isToday() {
            return moment().format('DD/MM/YYYY') === moment(this.activatedAt).format('DD/MM/YYYY');
        },
        isYesterday() {
            return (
                moment().subtract(1, 'days').format('DD/MM/YYYY') ===
                moment(this.activatedAt).format('DD/MM/YYYY')
            );
        },
        gatePdf() {
            return this.isTrial ? null : this.pdf;
        },
        allAccept() {
            return this.acceptGlobal && this.conditions.every((condition) => condition.accept);
        },
    },
    created() {
        this.getConformityState();

        EventBus.on('conformity:generate', (event) => {
            this.pdf = event.pdf.url;
        });
    },
    unmounted() {
        EventBus.off('conformity:generate');
    },
    methods: {
        closeModal(isConfirm) {
            if (isConfirm) {
                this.isConform = isConfirm.data.active;
                this.setConformity(isConfirm);
            }
            this.displayForm = false;
        },
        getConformityState() {
            this.isLoading = true;
            ApiClient.get(
                this.$router.resolve({ name: 'invoicing.account.conformities' }).href,
            ).then((response) => {
                this.isConform = response.data.active;
                if (this.isConform) {
                    this.setConformity(response);
                }

                this.isLoading = false;
            });
        },
        setConformity(response) {
            this.activatedAt = response.data.activated_at;
            this.activatedBy = response.data.activated_by;
            if (response.data.pdf) {
                this.pdf = response.data.pdf;
            }
        },
        confirm() {
            this.loading = true;
            ApiClient.post(
                this.$router.resolve({ name: 'invoicing.account.conformities' }).href,
                {},
                { confirm_final_change: true },
            )
                .then((response) => {
                    this.conformityResponse = response;
                    if (response.data.active) {
                        this.isConfirm = true;
                    }

                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                    if (error.response && error.response.status == 403) {
                        Push.error(
                            window.i18n.__(
                                'Vous devez activer la numérotation brouillon et vérifier les dates de vos documents.',
                            ),
                            window.i18n.__("Impossible d'activer la conformité sur votre compte."),
                        );
                    } else {
                        Push.error(
                            this.$l10n.__(
                                'Une erreur est survenue pendant le traitement de votre demande.',
                            ),
                            false,
                        );
                    }
                });
        },
    },
};
</script>

<style lang="scss">
.conformity {
    .dialog {
        &__default {
            padding-bottom: 24px;

            h3 {
                margin: 0 0 8px;
            }

            .el-checkbox {
                display: flex;
                align-items: center;
            }

            .fix-margin-p {
                margin-top: 0;
            }
        }

        &__footer {
            display: flex;
            justify-content: flex-end;
            padding: 0 32px 24px 32px;
        }
    }
}

.confirm-section {
    .success {
        font-size: 80px;
    }
}
</style>
